import { useEffect, useState } from 'react';

import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';

import { setMessage } from '../features/toasts/toastSlice';
import { useTranslation } from 'react-i18next';
import { handleAxiosErrorMessage } from '../utils';

const filterValidOrders = (orders) => {
  return Array.isArray(orders) ? orders.filter(Boolean) : [];
};

const useRefreshOrder = ({ apiCall, filters }) => {
  const { t } = useTranslation(['system']);
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(null);
  const [orderedByOptimizedRoute, setOrderedByOptimizedRoute] = useState(false);
  const [canReOptimizedRoute, setCanReOptimizedRoute] = useState(false);

  const dispatch = useDispatch();

  const { ref: bottomRef, inView: bottomInView } = useInView({
    threshold: 0.5,
  });

  const { ref: topRef, inView: topInView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    const sendRequest = async () => {
      setIsLoading(true);

      try {
        const response = await apiCall(filters);

        const {
          total_pages: totalPages,
          current_page: currentPage,
          orders,
          count,
          ordered_by_optimized_route: orderedByOptimizedRoute,
          re_optimize,
        } = response.data;

        const validOrders = filterValidOrders(orders);

        let newOrders;
        const { page, ...rest } = filters;

        if (currentPage > 1) {
          const beforePageResponse = await apiCall({ page: page - 1, ...rest });

          const { orders: beforePageOrders } = beforePageResponse.data;

          const validBeforePageOrders = filterValidOrders(beforePageOrders);

          newOrders = [...validBeforePageOrders, ...validOrders];

          if (page < totalPages) {
            newOrders[newOrders.length - 1].ref = bottomRef;
          }
        } else {
          newOrders = validOrders;

          if (newOrders.length > 0 && page < totalPages) {
            newOrders[newOrders.length - 1].ref = bottomRef;
          }
        }

        if (newOrders.length > 0 && page > 2) {
          newOrders[0].ref = topRef;
        }

        newOrders = newOrders.map((order) => {
          order.showMore = false;

          return order;
        });

        setOrders(newOrders);
        setTotalPages(totalPages);
        setCount(count);
        setCurrentPage(currentPage);
        setOrderedByOptimizedRoute(orderedByOptimizedRoute);
        setCanReOptimizedRoute(re_optimize ? re_optimize : false);
      } catch (err) {
        const errorMessage = handleAxiosErrorMessage(err);
        dispatch(
          setMessage({
            body: t(errorMessage, { ns: 'system' }),
            type: 'error',
          })
        );
      }

      setIsLoading(false);
    };

    sendRequest();
  }, [apiCall, dispatch, filters, bottomRef, topRef, t]);

  return [
    {
      isLoading,
      orders,
      currentPage,
      totalPages,
      bottomInView,
      topInView,
      count,
      orderedByOptimizedRoute,
      canReOptimizedRoute,
    },
  ];
};

export default useRefreshOrder;
